import React, { FC, memo, useEffect, useRef } from 'react';
import styled from '@emotion/styled';

/** How long one roll takes. */
const ROLL_MS = 260;

/**
 * A clipped window exactly one line tall. The current value sits in normal
 * flow; when it changes, the previous value is drawn on top and both roll -
 * up when the number grew, down when it shrank - like an old digital clock.
 */
const Window = styled.span`
  position: relative;
  display: inline-block;
  overflow: hidden;
  line-height: 1;
  vertical-align: baseline;
`;

/** The new value: enters from below (up) or from above (down). */
const Current = styled.span`
  display: block;
  white-space: nowrap;

  &.roll-up {
    animation: eposRollInUp ${ROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  &.roll-down {
    animation: eposRollInDown ${ROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @keyframes eposRollInUp {
    from {
      transform: translate3d(0, 100%, 0);
    }
    to {
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes eposRollInDown {
    from {
      transform: translate3d(0, -100%, 0);
    }
    to {
      transform: translate3d(0, 0, 0);
    }
  }
`;

/** The old value: leaves the way the new one came in, then unmounts by key. */
const Previous = styled.span`
  position: absolute;
  inset: 0;
  display: block;
  white-space: nowrap;
  pointer-events: none;

  &.roll-up {
    animation: eposRollOutUp ${ROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  &.roll-down {
    animation: eposRollOutDown ${ROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  @keyframes eposRollOutUp {
    from {
      transform: translate3d(0, 0, 0);
    }
    to {
      transform: translate3d(0, -100%, 0);
    }
  }

  @keyframes eposRollOutDown {
    from {
      transform: translate3d(0, 0, 0);
    }
    to {
      transform: translate3d(0, 100%, 0);
    }
  }
`;

interface Props {
  value: number;
  className?: string;
  testId?: string;
}

/**
 * Odometer-style number: every change rolls the digits, up for an increase
 * and down for a decrease. Use it for quantities - anything the shopper steps
 * with "+" and "−". Sums and counts that should draw the eye use the hop
 * (useHopOnChange) instead.
 *
 * No state: the previous value lives in a ref, and `key={value}` restarts the
 * CSS animation, so a change renders only this element.
 */
const RollingNumber: FC<Props> = ({ value, className, testId }) => {
  const previousRef = useRef<number | null>(null);
  const previous = previousRef.current;

  useEffect(() => {
    previousRef.current = value;
  }, [value]);

  const changed = previous !== null && previous !== value;
  const direction = changed ? (value > (previous as number) ? 'roll-up' : 'roll-down') : undefined;

  return (
    <Window className={className} data-testid={testId}>
      <Current key={value} className={direction}>
        {value}
      </Current>
      {changed && (
        <Previous key={`prev-${value}`} className={direction} aria-hidden>
          {previous}
        </Previous>
      )}
    </Window>
  );
};

export default memo(RollingNumber);
